import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { DragEndEvent } from '@dnd-kit/core';
import { useAppDispatch, useAppSelector } from 'hooks';

import { ContainerLoading, ErrorResponse } from '~components';
import DndWrapper from '~components/drag-and-drop/DndWrapper';

import { AuthTags } from '~constants/authTags';
import { selectUserTags } from '~main/selectors/login';

import { useToast } from '~libs/toast';
import { getErrorMessage } from '~libs/axiosWrapper';

import {
  useGetTemplateQuery,
  useUpdateTemplateMutation,
  useUpdateDraftVersionMutation,
  usePublishTemplateMutation,
  useCreateVersionMutation,
} from '~forms/actions/templates';
import { useGetMergeFieldsQuery } from '~forms/actions/mergeFields';
import {
  addField,
  buildFieldSchema,
  markClean,
  reorderFields,
  resetBuilder,
  setFields,
} from '~forms/reducers/formBuilderSlice';
import { makeDefaultField } from '~forms/lib/fieldFactory';
import { selectFields, selectIsDirty } from '~forms/selectors/formBuilder';
import { FormFieldType, FormTemplateVersion } from '~forms/types';
import {
  CANVAS_BOTTOM_DROP_ID,
  CANVAS_DROPPABLE_ID,
  FieldPalette,
  FieldProperties,
  FormBuilderLayout,
  FormCanvas,
  FormPreview,
  FormSettingsModal,
  FormTopBar,
} from '.';

const AUTOSAVE_DEBOUNCE_MS = 800;

const CANVAS_IDS = new Set([CANVAS_DROPPABLE_ID, CANVAS_BOTTOM_DROP_ID]);

function findDraftVersion(versions: FormTemplateVersion[]): FormTemplateVersion | null {
  const drafts = versions.filter((v) => v.published_at === null);
  if (drafts.length === 0) return null;
  return drafts.reduce((max, v) => (v.version_number > max.version_number ? v : max), drafts[0]);
}

const FormTemplateBuilder: React.FC = () => {
  const { id: templateId } = useParams<{ id: string }>(); // form template id is a UUID — never parseInt it
  const dispatch = useAppDispatch();
  const { showToast } = useToast();

  const fields = useAppSelector(selectFields);
  const isDirty = useAppSelector(selectIsDirty);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Editing/publishing requires the forms-customforms:Create tag; the API is the source of truth and
  // enforces it on every write. The builder route (AppRoutes) already gates entry on the same tag, so
  // on mount canEdit is effectively true here. canEdit is kept as a second, render-time layer because
  // the route guard only checks once on mount: if the tag is revoked mid-session the canvas, cog and
  // Save go read-only without needing a re-navigation. It also lets FormTopBar/FormSettingsModal be
  // driven purely by this prop (and unit-tested in both states).
  const tags = useAppSelector(selectUserTags);
  const canEdit = tags.includes(AuthTags.FORMS.CUSTOMFORMS.CREATE);

  const {
    data: template,
    isLoading: isTemplateLoading,
    isError: isTemplateError,
    error: templateError,
  } = useGetTemplateQuery(templateId ?? '', { skip: !templateId });

  const { data: mergeFieldGroups = {} } = useGetMergeFieldsQuery();

  const [updateTemplate] = useUpdateTemplateMutation();
  const [updateDraftVersion, { isLoading: isSaving }] = useUpdateDraftVersionMutation();
  const [publishTemplate, { isLoading: isPublishing }] = usePublishTemplateMutation();
  const [createVersion, { isLoading: isCreatingVersion }] = useCreateVersionMutation();

  // Version model: there is at most one open draft (published_at === null). Publishing locks it; the user
  // then explicitly creates the next draft. The version selector lets you view any version (read-only
  // unless it's the open draft).
  const versions = template?.versions ?? [];
  const draftVersion = findDraftVersion(versions);
  const latestVersion = versions.reduce<FormTemplateVersion | null>(
    (max, v) => (!max || v.version_number > max.version_number ? v : max),
    null,
  );
  const hasOpenDraft = !!draftVersion;

  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(null);

  // Default/repair the selection: prefer the open draft, else the latest version. Idempotent — only
  // fires when the current selection no longer exists, so re-running on any version change is safe.
  useEffect(() => {
    if (versions.length === 0) return;
    const stillExists = selectedVersionId && versions.some((v) => v.id === selectedVersionId);
    if (!stillExists) {
      const fallback = draftVersion ?? latestVersion;
      setSelectedVersionId(fallback ? fallback.id : null);
    }
  }, [versions, selectedVersionId, draftVersion, latestVersion]);

  const selectedVersion = versions.find((v) => v.id === selectedVersionId) ?? draftVersion ?? latestVersion ?? null;
  const viewingDraft = !!selectedVersion && selectedVersion.published_at === null;

  // Seed slice from the selected version. Re-runs only when the selected version (or template) changes —
  // not on autosave refetches (same id), so local edits and focus are preserved.
  useEffect(() => {
    if (selectedVersion?.field_schema?.fields) {
      dispatch(setFields(selectedVersion.field_schema.fields));
    } else if (template) {
      dispatch(setFields([]));
    }
    return () => {
      dispatch(resetBuilder());
    };
  }, [selectedVersion?.id, template?.id]); // intentional: seed only when IDs change

  // Debounced autosave
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fieldsRef = useRef(fields);
  const isDirtyRef = useRef(isDirty);
  // canEdit is read inside the debounced timeout below; mirror it into a ref (like isDirty) so the
  // callback always sees the current permission, even if it changes within the debounce window.
  const canEditRef = useRef(canEdit);
  fieldsRef.current = fields;
  isDirtyRef.current = isDirty;
  canEditRef.current = canEdit;

  useEffect(() => {
    if (!isDirty) return;
    // Only the open draft is editable/savable, and only with edit permission.
    if (!canEdit || !viewingDraft || !selectedVersion) return;
    if (!templateId) return;

    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);

    autosaveTimerRef.current = setTimeout(async () => {
      if (!isDirtyRef.current || !canEditRef.current) return;
      try {
        await updateDraftVersion({
          id: templateId,
          versionId: selectedVersion.id,
          field_schema: buildFieldSchema(fieldsRef.current),
        }).unwrap();
        dispatch(markClean());
      } catch (err) {
        showToast('danger', `Auto-save failed: ${getErrorMessage(err)}`);
      }
    }, AUTOSAVE_DEBOUNCE_MS);

    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    };
  }, [isDirty, fields, selectedVersion?.id, viewingDraft]); // intentional deps

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (!over) return;

      const activeData = active.data.current as Record<string, unknown> | undefined;
      const overId = over.id as string;
      // The only registered droppables in this DnD context are the canvas drop-zones and the sortable
      // field cards, so a valid canvas drop target is either a canvas zone id or an existing field id.
      const fieldIds = new Set(fieldsRef.current.map((f) => f.id));

      // Palette field dropped on the canvas (a drop-zone, or onto/beside an existing field card)
      if (activeData?.type === 'palette-field' && (CANVAS_IDS.has(overId) || fieldIds.has(overId))) {
        const fieldType = activeData.fieldType as FormFieldType | undefined;
        if (!fieldType) return;
        dispatch(addField(makeDefaultField(fieldType)));
        return;
      }

      // Canvas reorder — both active and over must be existing field cards
      const activeId = active.id as string;
      if (fieldIds.has(activeId) && fieldIds.has(overId) && activeId !== overId) {
        dispatch(reorderFields({ activeId, overId }));
      }
    },
    [dispatch],
  );

  const handleUpdateSettings = async (settings: {
    title: string;
    description: string;
    allow_care_worker_fill: boolean;
  }) => {
    if (!templateId) return;
    try {
      await updateTemplate({ id: templateId, ...settings }).unwrap();
      showToast('success', 'Form settings updated');
    } catch (err) {
      showToast('danger', `Failed to update form settings: ${getErrorMessage(err)}`);
      throw err; // re-throw so FormSettingsModal stays open on failure
    }
  };

  const handlePublish = async () => {
    if (!templateId) return;
    try {
      await publishTemplate(templateId).unwrap();
      showToast('success', 'Form published — this version is now locked');
    } catch (err) {
      showToast('danger', `Publish failed: ${getErrorMessage(err)}`);
    }
  };

  const handleCreateVersion = async () => {
    if (!templateId) return;
    try {
      const result = await createVersion(templateId).unwrap();
      setSelectedVersionId(result.draft_version.id); // jump to the new editable draft
      showToast('success', `New draft version created (v${result.draft_version.version_number})`);
    } catch (err) {
      showToast('danger', `Could not create a new version: ${getErrorMessage(err)}`);
    }
  };

  const mergeRegistry = useMemo(() => Object.values(mergeFieldGroups).flat(), [mergeFieldGroups]);

  if (!templateId) return <div className="p-3 text-danger">Invalid template ID</div>;
  if (isTemplateLoading) return <ContainerLoading loading />;
  if (isTemplateError || !template) return <ErrorResponse message={getErrorMessage(templateError)} />;

  // Editable only when the user can edit AND is viewing the open draft.
  const isCanvasReadOnly = !canEdit || !viewingDraft;

  return (
    <div className="form-builder">
      <FormTopBar
        template={template}
        versions={versions}
        selectedVersionId={selectedVersion?.id ?? null}
        onSelectVersion={setSelectedVersionId}
        hasOpenDraft={hasOpenDraft}
        canEdit={canEdit}
        isPublishing={isPublishing}
        isCreatingVersion={isCreatingVersion}
        onPublish={handlePublish}
        onCreateVersion={handleCreateVersion}
        onPreview={() => setPreviewOpen(true)}
        onOpenSettings={() => setSettingsOpen(true)}
      />
      <DndWrapper onDragEnd={handleDragEnd} canScroll={true}>
        <FormBuilderLayout
          palette={<FieldPalette mergeFieldGroups={mergeFieldGroups} readOnly={isCanvasReadOnly} />}
          canvas={
            <FormCanvas
              fields={fields}
              readOnly={isCanvasReadOnly}
              registry={mergeRegistry}
              onCreateVersion={isCanvasReadOnly && canEdit && !hasOpenDraft ? handleCreateVersion : undefined}
            />
          }
          properties={<FieldProperties readOnly={isCanvasReadOnly} />}
        />
      </DndWrapper>
      {isSaving && <span className="visually-hidden" aria-live="polite">Auto-saving...</span>}
      <FormPreview
        isOpen={previewOpen}
        toggle={() => setPreviewOpen(false)}
        title={template.title}
        fields={fields}
        registry={mergeRegistry}
      />
      <FormSettingsModal
        isOpen={settingsOpen}
        setIsOpen={setSettingsOpen}
        template={template}
        canEdit={canEdit}
        onSave={handleUpdateSettings}
      />
    </div>
  );
};

export default FormTemplateBuilder;
